import * as Sentry from '@sentry/react';
import { GridState } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { getTrackableViewId, serializeViewForCreation, ViewModel } from '../../../models/view.model';
import { promoteView } from '../../../services/queries/MaggieViewQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { DefaultIgnoreList, getStateToPersist } from '../../../util/ag-grid-utils';
import { trackEvent } from '../../../util/datadogTracking';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { pfGridStateAtom } from '../components/OverviewTable/PfState';
import { pageConfigPFJ } from '../state/PageStateJ';
import { useInvalidateViewState } from '../state/ViewStateJ';
import { PromoteViewFields } from './PromoteViewForm';

export function usePromoteView() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateViewState = useInvalidateViewState();
  const gridState = useAtomValue(pfGridStateAtom);
  const addViewToPageConfig = useAtomCallback(
    useCallback((get, set, viewId: string) => {
      const pageConfig = get(pageConfigPFJ);
      if (pageConfig instanceof Promise) {
        return;
      }
      if (pageConfig.order.includes(viewId)) {
        return;
      }
      set(pageConfigPFJ, { ...pageConfig, order: [viewId, ...pageConfig.order] });
    }, [])
  );

  return useCallback(
    async (view: ViewModel, promoteViewFields: PromoteViewFields) => {
      const payload = prepareViewForPromotion(view, promoteViewFields.name, gridState);
      const landingViewId = PreferencesServiceV3.get().getPreference(
        getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
        PreferenceScope.ORGANIZATION
      );
      try {
        const promotedView = await promoteView(payload, {
          viewToReplace: promoteViewFields.viewToReplace,
        });
        if (promoteViewFields.setAsLandingView || promoteViewFields.viewToReplace === landingViewId) {
          await PreferencesServiceV3.get().setPreference(
            getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
            promotedView.id,
            PreferenceScope.ORGANIZATION
          );
        }
        await invalidateViewState();
        addViewToPageConfig(promotedView.viewId);
        // Source the new view's identity directly from the promote result
        // (`promoteView` returns the deserialized ViewModel of the created view).
        trackEvent('portfolio.view_created', {
          view_id: getTrackableViewId(promotedView),
          view_type: promotedView.type,
          view_data_type: promotedView.viewType,
        });
        pushSuccessToast({ message: 'View promoted successfully' });
        return promotedView;
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to promote view');
        Sentry.logger.error(message);
        pushErrorToast({ message });
      }
    },
    [addViewToPageConfig, invalidateViewState, pushErrorToast, pushSuccessToast, gridState]
  );
}

export function prepareViewForPromotion(view: ViewModel, newName: string, gridState: GridState | null) {
  const preppedView = cloneDeep(view);
  preppedView.origin = null;
  preppedView.label = newName;
  preppedView.name = newName;
  if (gridState) {
    // FD-3283: strip ephemeral slices (scroll/focusedCell/cellSelection) so a
    // promoted default opens clean — same as the duplicate/persist path.
    preppedView.gridState = getStateToPersist(gridState, DefaultIgnoreList);
  }

  return serializeViewForCreation(preppedView);
}
